import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, MailCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BlueprintBackground } from "@/components/BlueprintBackground";
import { Logo } from "@/components/Logo";
import { Wordmark } from "@/components/Wordmark";
import { useAuth } from "@/contexts/AuthContext";

export default function Signup() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { register } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    setLoading(true);
    try {
      await register(email, password, name);
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Sign up failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" data-testid="signup-page">
      <BlueprintBackground />
      <div className="w-full max-w-sm">
        <Link to="/" className="flex items-center justify-center gap-2 mb-8" data-testid="signup-logo-link">
          <Logo size={26} />
          <Wordmark className="font-heading text-xl font-bold tracking-tight text-white" />
        </Link>
        <div className="rounded-2xl border border-slate-700/60 bg-slate-900/80 backdrop-blur-md p-8 shadow-2xl">
          {submitted ? (
            <div className="text-center" data-testid="signup-check-email-panel">
              <MailCheck className="text-blue-400 mx-auto mb-4" size={32} />
              <h1 className="font-heading text-xl font-bold text-white mb-2">Check your email</h1>
              <p className="text-slate-400 text-sm mb-6">
                We've sent a verification link to <span className="text-slate-200">{email}</span>. Click it to activate your
                account, then come back and log in.
              </p>
              <Link to="/login" className="text-blue-400 hover:text-blue-300 font-medium text-sm" data-testid="signup-go-to-login-link">
                Go to log in
              </Link>
            </div>
          ) : (
            <>
              <h1 className="font-heading text-2xl font-bold text-white mb-1">Create your account</h1>
              <p className="text-slate-400 text-sm mb-6">Start modeling your enterprise architecture in minutes.</p>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="name" className="text-slate-300">Name</Label>
                  <Input
                    id="name"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    data-testid="signup-name-input"
                    className="mt-1.5 bg-slate-800/60 border-slate-700 text-white placeholder:text-slate-500"
                    placeholder="Jane Architect"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-slate-300">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="signup-email-input"
                    className="mt-1.5 bg-slate-800/60 border-slate-700 text-white placeholder:text-slate-500"
                    placeholder="you@company.com"
                  />
                </div>
                <div>
                  <Label htmlFor="password" className="text-slate-300">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="signup-password-input"
                    className="mt-1.5 bg-slate-800/60 border-slate-700 text-white placeholder:text-slate-500"
                    placeholder="At least 6 characters"
                  />
                </div>
                {error && (
                  <p className="text-sm text-rose-400" data-testid="signup-error-message">{error}</p>
                )}
                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="signup-submit-button"
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                >
                  {loading ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
                  Create Account
                </Button>
              </form>
              <p className="text-sm text-slate-400 mt-6 text-center">
                Already have an account?{" "}
                <Link to="/login" className="text-blue-400 hover:text-blue-300 font-medium" data-testid="signup-login-link">
                  Log in
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
